<template>
  <div class="w-full">
    <h1 class="mb-8 text-3xl font-bold">
      Usługi
    </h1>
    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="service-filter-name"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Nazwa
        </label>
        <input
          id="service-filter-name"
          v-model="form.name"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="service-filter-category"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Kategoria
        </label>
        <service-category-select
          id="service-filter-category"
          v-model="form.category"
          :options="categoryFilterOptions"
          allow-blank
          blank-label="Wszystkie"
          class="w-[240px]"
          compact
        />
      </div>
      <div>
        <label
          for="service-filter-specialist"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Specjalista
        </label>
        <input
          id="service-filter-specialist"
          v-model="form.specialist"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
      <button class="btn-indigo ml-auto" @click="modalNew = true">
        Dodaj usługę
      </button>
      <modal
        :open="modalNew"
        title="Dodaj usługę"
        @close="modalNew = false"
      >
        <new-service
          :available_specialists="available_specialists"
          :category_options="category_options"
          @success="modalNew = false"
        />
      </modal>
    </div>
    <div
      v-if="selectedIds.length"
      class="mb-4 flex flex-wrap items-end gap-x-4 gap-y-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3"
    >
      <p class="text-sm font-medium text-indigo-900">
        Zaznaczono: {{ selectedIds.length }}
      </p>
      <div>
        <label
          for="service-bulk-category"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Kategoria
        </label>
        <service-category-select
          id="service-bulk-category"
          v-model="bulkCategory"
          :options="category_options"
          allow-blank
          highlight-when-blank
          blank-label="Wybierz kategorię"
          class="w-[240px]"
          compact
        />
      </div>
      <button
        type="button"
        class="btn-indigo"
        :disabled="bulkApplying"
        @click="applyBulkCategory"
      >
        Przypisz kategorię
      </button>
      <button
        type="button"
        class="text-sm text-gray-600 hover:text-gray-900"
        :disabled="bulkApplying"
        @click="clearSelection"
      >
        Wyczyść zaznaczenie
      </button>
    </div>
    <div class="overflow-x-auto rounded-sm bg-white shadow-sm">
      <table
        class="w-full whitespace-nowrap"
        :class="{ 'is-dnd-dragging': dragging }"
      >
        <thead>
          <tr class="text-left font-bold">
            <th class="w-px px-4 pb-4 pt-6">
              <input
                ref="selectAllCheckbox"
                type="checkbox"
                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                :checked="allPageSelected"
                aria-label="Zaznacz wszystkie na stronie"
                @change="toggleSelectAll"
              >
            </th>
            <th class="w-px px-4 pb-4 pt-6" />
            <th class="px-6 pb-4 pt-6 text-green-700">
              Nazwa
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Kategoria
            </th>
            <th class="px-6 pb-4 pt-6 text-right text-green-700">
              Cena
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Specjaliści
            </th>
            <th class="px-6 pb-4 pt-6 text-green-700">
              Dane aktualizacji
            </th>
            <th class="px-6 pb-4 pt-6 text-center">
              <service-content-column-header />
            </th>
            <th class="px-6 pb-4 pt-6">
              Aktywna
            </th>
            <th class="px-6 pb-4 pt-6" />
          </tr>
        </thead>
        <draggable
          v-if="localServices.length"
          v-model="localServices"
          tag="tbody"
          item-key="id"
          handle=".drag-handle"
          ghost-class="dnd-drop-ghost"
          chosen-class="dnd-chosen"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: service }">
            <tr
              :key="service.id"
              :class="[
                'focus-within:bg-gray-100 hover:bg-gray-100',
                isSelected(service.id) ? 'bg-indigo-50/60' : '',
              ]"
            >
              <td class="w-px border-t px-4">
                <input
                  type="checkbox"
                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  :checked="isSelected(service.id)"
                  :aria-label="`Zaznacz ${service.name}`"
                  @click.stop
                  @change="toggleSelected(service.id)"
                >
              </td>
              <td class="w-px border-t px-4">
                <div class="drag-handle cursor-grab active:cursor-grabbing" title="Przeciągnij, aby zmienić kolejność">
                  <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                  </svg>
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  {{ service.name }}
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <service-category-select
                    :model-value="service.category"
                    :options="category_options"
                    :disabled="updatingCategoryId === service.id"
                    allow-blank
                    highlight-when-blank
                    blank-label="Wybierz kategorię"
                    class="max-w-[240px]"
                    compact
                    @update:model-value="updateCategory(service, $event)"
                  />
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center justify-end px-6 py-4 tabular-nums">
                  {{ formatPrice(service.price) }}
                </div>
              </td>
              <td class="border-t">
                <div class="flex flex-wrap items-center gap-1.5 px-6 py-4">
                  <Link
                    v-for="specialist in service.specialists"
                    :key="specialist.id"
                    :href="specialistEditRoute(specialist.id)"
                    :title="specialistLabel(specialist)"
                    class="inline-flex shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  >
                    <img
                      v-if="specialist.photo_url"
                      :src="specialist.photo_url"
                      :alt="specialist.full_name"
                      class="h-7 w-7 rounded-full object-cover ring-1 ring-gray-200 transition hover:ring-indigo-300"
                    >
                    <span
                      v-else
                      class="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-600 ring-1 ring-gray-200 transition hover:ring-indigo-300"
                    >
                      {{ specialistInitials(specialist) }}
                    </span>
                  </Link>
                  <span
                    v-if="!service.specialists || !service.specialists.length"
                    class="text-gray-400"
                  >
                    —
                  </span>
                </div>
              </td>
              <td class="border-t">
                <record-update-dates
                  :synced-at="service.synced_at"
                  :updated-at="service.updated_at"
                />
              </td>
              <td class="border-t">
                <div class="flex items-center justify-center px-6 py-4">
                  <content-missing-dot :items="service.missing_content || []" />
                </div>
              </td>
              <td class="border-t">
                <div class="flex items-center px-6 py-4">
                  <toggle-switch
                    :model-value="service.active"
                    :disabled="togglingId === service.id"
                    @update:model-value="toggleActive(service)"
                  />
                </div>
              </td>
              <td class="w-px border-t">
                <div class="ml-4 flex shrink-0 items-center gap-2 px-4">
                  <a
                    v-if="service.id && service.slug"
                    class="btn-action-view"
                    :href="$routes.service(service.slug)"
                    target="_blank"
                    title="Zobacz na stronie"
                  >
                    <icon name="eye" class="mr-1.5 h-3.5 w-3.5" />
                    Podgląd na stronie
                  </a>
                  <Link
                    v-if="service.id"
                    class="btn-action-edit"
                    :href="editRoute(service.id)"
                    title="Edytuj"
                  >
                    <icon name="edit" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Edytuj
                  </Link>
                  <button
                    v-if="service.id"
                    type="button"
                    class="btn-action-delete"
                    title="Usuń"
                    @click.stop="destroy(service)"
                  >
                    <icon name="trash" class="mr-1.5 h-3.5 w-3.5 stroke-current" />
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          </template>
        </draggable>
        <tbody v-else>
          <tr>
            <td class="border-t px-6 py-4" colspan="10">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination :meta="services.meta" />
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import Modal from '@/Shared/Modal.vue';
import NewService from '@/Pages/Admin/Services/_New.vue';
import throttle from 'lodash/throttle';
import { createServiceRouteHelper } from '@/utils/createServiceRouteHelper';
import { createSpecialistRouteHelper } from '@/utils/createSpecialistRouteHelper';
import { createLayoutHelper } from '@/utils/layoutHelper';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import ServiceCategorySelect from '@/Shared/ServiceCategorySelect.vue';
import RecordUpdateDates from '@/Shared/RecordUpdateDates.vue';
import ContentMissingDot from '@/Shared/ContentMissingDot.vue';
import ServiceContentColumnHeader from '@/Shared/ServiceContentColumnHeader.vue';
import draggable from 'vuedraggable';
import axios from 'axios';

export default {
  components: {
    Link,
    Icon,
    ServiceCategorySelect,
    RecordUpdateDates,
    ContentMissingDot,
    ServiceContentColumnHeader,
    Pagination,
    Modal,
    NewService,
    ToggleSwitch,
    draggable,
  },
  layout: createLayoutHelper,
  props: {
    services: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line vue/prop-name-casing
    available_specialists: {
      type: Array,
      default: () => [],
    },
    // eslint-disable-next-line vue/prop-name-casing
    category_options: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        name: this.filters.name || '',
        category: this.filters.category || null,
        specialist: this.filters.specialist || '',
      },
      modalNew: false,
      localServices: [...this.services.data],
      dragging: false,
      togglingId: null,
      updatingCategoryId: null,
      selectedIds: [],
      bulkCategory: null,
      bulkApplying: false,
    };
  },
  computed: {
    routes() {
      return createServiceRouteHelper(null, this.$routes);
    },
    specialistRoutes() {
      return createSpecialistRouteHelper(null, this.$routes);
    },
    categoryFilterOptions() {
      return [
        { value: '__none__', label: 'Bez kategorii' },
        ...this.category_options,
      ];
    },
    hasActiveFilters() {
      return !!(
        this.form.name?.trim() ||
        this.form.category ||
        this.form.specialist?.trim()
      );
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak usług pasujących do filtrów.'
        : 'Nie znaleziono usług.';
    },
    allPageSelected() {
      return (
        this.localServices.length > 0 &&
        this.localServices.every((service) => this.isSelected(service.id))
      );
    },
    somePageSelected() {
      return this.localServices.some((service) => this.isSelected(service.id));
    },
  },
  watch: {
    somePageSelected: {
      handler() {
        this.syncSelectAllIndeterminate();
      },
      immediate: true,
    },
    allPageSelected() {
      this.syncSelectAllIndeterminate();
    },
    localServices() {
      this.syncSelectAllIndeterminate();
    },
    form: {
      handler: throttle(function () {
        const query = pickBy({
          name: this.form.name?.trim(),
          category: this.form.category || undefined,
          specialist: this.form.specialist?.trim(),
        });
        this.$inertia.get(
          this.routes.index(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['services', 'filters'],
          },
        );
      }, 150),
      deep: true,
    },
    '$page.url': {
      handler() {
        if (this.modalNew) {
          this.modalNew = false;
        }
      },
    },
    'services.data': {
      handler(newData) {
        this.localServices = [...newData];
      },
      deep: true,
    },
  },
  methods: {
    isSelected(serviceId) {
      return this.selectedIds.includes(serviceId);
    },
    toggleSelected(serviceId) {
      if (this.isSelected(serviceId)) {
        this.selectedIds = this.selectedIds.filter((id) => id !== serviceId);
      } else {
        this.selectedIds = [...this.selectedIds, serviceId];
      }
    },
    toggleSelectAll(event) {
      const pageIds = this.localServices.map((service) => service.id);
      if (event.target.checked) {
        this.selectedIds = [...new Set([...this.selectedIds, ...pageIds])];
      } else {
        const pageIdSet = new Set(pageIds);
        this.selectedIds = this.selectedIds.filter((id) => !pageIdSet.has(id));
      }
    },
    clearSelection() {
      this.selectedIds = [];
      this.bulkCategory = null;
    },
    syncSelectAllIndeterminate() {
      this.$nextTick(() => {
        const checkbox = this.$refs.selectAllCheckbox;
        if (!checkbox) return;
        checkbox.indeterminate = this.somePageSelected && !this.allPageSelected;
      });
    },
    async applyBulkCategory() {
      if (!this.selectedIds.length || this.bulkApplying) return;

      this.bulkApplying = true;

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(
          this.routes.bulkUpdateCategory(),
          {
            ids: this.selectedIds,
            category: this.bulkCategory,
          },
          {
            headers: {
              'X-CSRF-Token': csrfToken,
            },
          },
        );

        this.clearSelection();
        this.$inertia.reload({ only: ['services'], preserveScroll: true });
      } catch (error) {
        console.error('Failed to bulk update categories:', error);
        alert('Nie udało się przypisać kategorii. Spróbuj ponownie.');
      } finally {
        this.bulkApplying = false;
      }
    },
    formatPrice(price) {
      if (price == null || price === '') return '—';
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: 'PLN',
      }).format(Number(price));
    },
    specialistLabel(specialist) {
      return [specialist.title, specialist.full_name].filter(Boolean).join(' ') || specialist.full_name;
    },
    specialistInitials(specialist) {
      const parts = (specialist.full_name || '').trim().split(/\s+/);
      if (!parts.length) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    },
    specialistEditRoute(specialistId) {
      return specialistId ? this.specialistRoutes.edit(specialistId) : '#';
    },
    resetFilters() {
      this.form.name = '';
      this.form.category = null;
      this.form.specialist = '';
    },
    updateCategory(service, category) {
      if (!service.id || this.updatingCategoryId) return;
      if ((service.category || null) === (category || null)) return;

      this.updatingCategoryId = service.id;
      const target = this.localServices.find((item) => item.id === service.id);
      const previousCategory = target?.category ?? service.category;

      if (target) {
        target.category = category;
        target.category_label = this.categoryLabelFor(category);
        target.missing_content = this.missingContentFor(target);
      }

      this.$inertia.put(
        this.routes.update(service.id),
        { service: { category } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['services'],
          onError: () => {
            if (target) {
              target.category = previousCategory;
              target.category_label = this.categoryLabelFor(previousCategory);
              target.missing_content = this.missingContentFor(target);
            }
          },
          onFinish: () => {
            this.updatingCategoryId = null;
          },
        },
      );
    },
    categoryLabelFor(category) {
      if (!category) return null;
      const option = this.category_options.find((item) => item.value === category);
      return option?.label || category;
    },
    missingContentFor(service) {
      const labels = [];
      if (!service.category) labels.push('Kategoria');
      // Other fields come from server; keep existing missing items except category
      const existing = (service.missing_content || []).filter((item) => item !== 'Kategoria');
      return [...existing, ...labels];
    },
    editRoute(serviceId) {
      return serviceId ? this.routes.edit(serviceId) : '#';
    },
    toggleActive(service) {
      if (!service.id || this.togglingId) return;
      this.togglingId = service.id;
      const target = this.localServices.find((item) => item.id === service.id);
      if (target) target.active = !target.active;

      this.$inertia.put(
        this.routes.update(service.id),
        { service: { active: target ? target.active : !service.active } },
        {
          preserveScroll: true,
          preserveState: true,
          only: ['services'],
          onError: () => {
            if (target) target.active = !target.active;
          },
          onFinish: () => {
            this.togglingId = null;
          },
        },
      );
    },
    destroy(service) {
      if (!service.id) return;
      if (confirm('Czy na pewno chcesz usunąć tę usługę?')) {
        this.$inertia.delete(this.routes.destroy(service.id), {
          preserveScroll: true,
        });
      }
    },
    async onDragEnd() {
      this.dragging = false;

      const ids = this.localServices.map((service) => service.id);

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        await axios.post(this.routes.reorder(), { ids }, {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        });

        this.$inertia.reload({ only: ['services'] });
      } catch (error) {
        console.error('Failed to reorder services:', error);
        this.localServices = [...this.services.data];
        alert('Nie udało się zmienić kolejności usług. Spróbuj ponownie.');
      }
    },
    onDragStart() {
      this.dragging = true;
    },
  },
};
</script>
